import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import DashboardLayout from "@/pages/DashboardLayout";
import DashboardHome from "@/pages/DashboardHome";
import OrganizationsPage from "@/pages/OrganizationsPage";
import ItemsPage from "@/pages/ItemsPage";
import InventoryPage from "@/pages/InventoryPage";
import TaxesPage from "@/pages/TaxesPage";
import CustomersPage from "@/pages/CustomersPage";
import CategoriesPage from "@/pages/CategoriesPage";
import InvoicesPage from "@/pages/InvoicesPage";
import InvoiceCreatePage from "@/pages/InvoiceCreatePage";
import InvoiceViewPage from "@/pages/InvoiceViewPage";
import PaymentsPage from "@/pages/PaymentsPage";
import ReportsPage from "@/pages/ReportsPage";
import SettingsPage from "@/pages/SettingsPage";
import PublicPaymentPage from "@/pages/PublicPaymentPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Organization Context
export const OrgContext = createContext(null);

export const useOrg = () => {
  const context = useContext(OrgContext);
  if (!context) {
    throw new Error("useOrg must be used within OrgProvider");
  }
  return context;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    setUser(response.data.user);
    localStorage.setItem('session_token', response.data.token);
    return response.data;
  };

  const register = async (name, email, password) => {
    const response = await axios.post(`${API}/auth/register`, { name, email, password });
    setUser(response.data.user);
    localStorage.setItem('session_token', response.data.token);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
    localStorage.removeItem('session_token');
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, logout, loginWithGoogle, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Org Provider
const OrgProvider = ({ children }) => {
  const [currentOrg, setCurrentOrg] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const { user } = useAuth();

  const fetchOrganizations = async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API}/organizations`, { withCredentials: true });
      setOrganizations(response.data);
      if (response.data.length > 0) {
        const defaultOrg = response.data.find(o => o.is_default) || response.data[0];
        setCurrentOrg(defaultOrg);
      }
    } catch (error) {
      console.error("Failed to fetch organizations:", error);
    }
  };

  useEffect(() => {
    if (user) {
      fetchOrganizations();
    }
  }, [user]);

  const switchOrg = (orgId) => {
    const org = organizations.find(o => o.org_id === orgId);
    if (org) {
      setCurrentOrg(org);
    }
  };

  return (
    <OrgContext.Provider value={{ currentOrg, setCurrentOrg, organizations, setOrganizations, fetchOrganizations, switchOrg }}>
      {children}
    </OrgContext.Provider>
  );
};

// Module-level flag to prevent StrictMode double-execution of OAuth callback
// useRef is reset when StrictMode unmounts/remounts, causing duplicate API calls
let _oauthProcessing = false;

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const [error, setError] = useState(null);

  useEffect(() => {
    if (_oauthProcessing) return;
    _oauthProcessing = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      console.log("OAuth callback - raw hash:", hash);
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const rawSessionId = sessionIdMatch[1];
        const sessionId = decodeURIComponent(rawSessionId).trim();
        console.log("OAuth session_id extracted, length:", sessionId.length);
        try {
          const response = await axios.post(`${API}/auth/session`, { session_id: sessionId }, { withCredentials: true });
          setUser(response.data.user);
          toast.success("Logged in successfully!");
          // Clear the hash before navigating
          window.history.replaceState(null, '', window.location.pathname);
          navigate('/dashboard', { replace: true });
        } catch (error) {
          console.error("Auth callback error:", error.response?.data || error.message);
          const message = error.response?.data?.detail || "Authentication failed. Please try again.";
          setError(message);
          toast.error(message);
        }
      } else {
        navigate('/login', { replace: true });
      }
      _oauthProcessing = false;
    };

    processAuth();
    return () => { /* cleanup - don't reset flag during StrictMode unmount */ };
  }, []);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md p-8">
          <div className="w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-destructive text-xl">!</span>
          </div>
          <h2 className="text-lg font-medium mb-2">Sign In Failed</h2>
          <p className="text-sm text-muted-foreground mb-6">{error}</p>
          <div className="flex gap-3 justify-center">
            <button onClick={() => navigate('/login', { replace: true })} className="px-4 py-2 bg-zinc-100 text-sm rounded hover:bg-zinc-200">
              Back to Login
            </button>
            <button onClick={() => { hasProcessed.current = false; setError(null); window.location.reload(); }} className="px-4 py-2 bg-primary text-white text-sm rounded hover:opacity-90">
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-muted-foreground">Completing sign in...</p>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// App Router
function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id - CRITICAL for OAuth
  if (location.hash?.includes('session_id=') || window.location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <OrgProvider>
            <DashboardLayout />
          </OrgProvider>
        </ProtectedRoute>
      }>
        <Route index element={<DashboardHome />} />
        <Route path="organizations" element={<OrganizationsPage />} />
        <Route path="items" element={<ItemsPage />} />
        <Route path="categories" element={<CategoriesPage />} />
        <Route path="inventory" element={<InventoryPage />} />
        <Route path="taxes" element={<TaxesPage />} />
        <Route path="customers" element={<CustomersPage />} />
        <Route path="invoices" element={<InvoicesPage />} />
        <Route path="invoices/new" element={<InvoiceCreatePage />} />
        <Route path="invoices/:id" element={<InvoiceViewPage />} />
        <Route path="invoices/:id/edit" element={<InvoiceCreatePage />} />
        <Route path="payments" element={<PaymentsPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>

      {/* Public Payment Page */}
      <Route path="/pay/:token" element={<PublicPaymentPage />} />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
